import type { RxReplicationWriteToMasterRow } from 'rxdb'

/**
 * Safely converts a timestamp to an ISO string.
 * For required date fields (createdOn, updatedOn) — falls back to current time.
 */
export function toISOString(value: number | string | null | undefined): string {
  if (value == null) return new Date().toISOString()
  const date = new Date(value)
  if (isNaN(date.getTime())) return new Date().toISOString()
  return date.toISOString()
}

/**
 * Safely converts a timestamp to an ISO string, or returns null.
 * For optional date fields (deletedOn, dueDate, startDate, etc.)
 */
export function toISOStringOrNull(value: number | string | null | undefined): string | null {
  if (value == null) return null
  const date = new Date(value)
  if (isNaN(date.getTime())) return null
  return date.toISOString()
}

const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Returns the value only if it is a valid GUID string, otherwise null.
 * Guards against legacy/local docs holding non-GUID ids (e.g. the literal
 * "task") in fields the backend deserializes as Guid? — sending those 400s
 * the whole push batch, and the bad doc can never clear on its own.
 */
export function toGuidOrNull(value: string | null | undefined): string | null {
  if (value == null) return null
  return GUID_RE.test(value) ? value : null
}

/**
 * Filters out malformed documents before push.
 * This prevents crashes when RxDB has stored error responses from
 * previous failed conflict resolutions as local document state.
 */
export function filterValidPushDocs<T>(
  docs: Array<RxReplicationWriteToMasterRow<T>>,
  collectionName: string,
): Array<RxReplicationWriteToMasterRow<T>> {
  return docs.filter((doc) => {
    const state = doc.newDocumentState as any
    if (state.error || (!state.createdOn && !state._deleted)) {
      console.warn(
        `[SYNC] Skipping malformed ${collectionName} document in push: ${state.id}`,
      )
      return false
    }
    return true
  })
}

/**
 * Filters out malformed conflict responses from the server.
 * Prevents error objects (e.g. {id, error, _deleted}) from being
 * stored as local document state by RxDB's conflict resolution.
 */
export function filterValidConflicts(conflicts: Array<any>): Array<any> {
  return conflicts.filter(
    (c) => !c.error && c.createdOn != null && c.updatedOn != null,
  )
}
